import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  colors,
  useTheme,
} from '@material-ui/core';
import { IProjectClient } from 'app/models/entities/IProjectClient';
import { IProject } from 'app/models/responses/IProject';
import { IProjectLearnerMetricResponse } from 'app/models/responses/IProjectLearnerMetricResponse';
import { ExceptionHandler } from 'components';
import DonutChart from 'components/_dashboard/Charts/DonutChart';
import { rootConfig } from 'config';
import useMounted from 'hooks/useMounted';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import axios from 'utils/axios';

interface IProps {
  project: IProject;
  projectClient: IProjectClient;
}

const Metrics: React.FC<IProps> = (props) => {
  const { project, projectClient } = props;
  const mounted = useMounted();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [metrics, setMetrics] = useState<IProjectLearnerMetricResponse | null>(null);

  const getMetrics = useCallback(async () => {
    try {
      const response = await axios.get(
        `${rootConfig.odataRoute}/projectClients(${projectClient.id})/getLearnerMetrics()`,
      );
      if (mounted.current) {
        setMetrics(response.data);
      }
    } catch (error) {
      enqueueSnackbar(<ExceptionHandler exception={error} />, { variant: 'error' });
    }
  }, [mounted, enqueueSnackbar, projectClient.id]);

  useEffect(() => {
    getMetrics();
  }, [getMetrics]);

  if (!metrics) return null;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Card>
          <CardHeader title='Learner Competence' />
          <Divider />
          <Box>
            <DonutChart
              series={[
                metrics.competentLearnerTotal,
                metrics.partiallyCompetentLearnerTotal,
                metrics.notYetCompetentLearnerTotal,
              ]}
              options={{
                labels: ['Competent', 'Partially Competent', 'Not Yet Competent'],
                colors: [colors.green[500], colors.amber[400], colors.orange[700]],
                theme: { mode: (theme.palette as any).mode },
              }}
            />
          </Box>
        </Card>
      </Box>
      <Card>
        <CardHeader title='Learner Metrics' />

        <Divider />
        <CardContent style={{ paddingTop: 0, paddingBottom: 0 }}>
          <List>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.learnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Total
                  </Typography>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.enrolledLearnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Enrolled
                  </Typography>
                }
              />
            </ListItem>

            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.abledLearnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Able Bodied
                  </Typography>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.disabledLearnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Disabled
                  </Typography>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.absorbedLearnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Absorbed
                  </Typography>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.droppedOffLearnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Dropped Off
                  </Typography>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.terminatedLearnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Terminated
                  </Typography>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.competentLearnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Competent
                  </Typography>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.partiallyCompetentLearnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Partially Competent
                  </Typography>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Typography variant='h6'>{metrics.notYetCompetentLearnerTotal}</Typography>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color='textPrimary' variant='subtitle2'>
                    Not Yet Competent
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Metrics;
